"use client";

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { CheckCircle, Calendar, Clock, Users, Phone, User } from "lucide-react";
import Header from "../common/header";
import Footer from "../common/footer";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { CheckoutPageAnimator } from "../../components/checkout-page-animator";
import { formatDateTime } from "../../lib/utils";

// Payment logos
const paymentLogos = [
  { src: "/global/visa.webp", alt: "Visa" },
  { src: "/global/mastercard.webp", alt: "Mastercard" },
  { src: "/global/applepay.webp", alt: "Apple Pay" },
  { src: "/global/paypal.webp", alt: "PayPal" },
];

const ResortBookingCheckout = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    resortBookingStore,
    paymentStore,
    userStore: { user, isPartner },
  } = useStore();

  const [booking, setBooking] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentType, setPaymentType] = useState("");
  const [error, setError] = useState("");

  // Fetch booking data
  useEffect(() => {
    const fetchBookingData = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const bookingData = await resortBookingStore.getBooking(id);
        setBooking(bookingData);
      } catch (error) {
        console.error("Failed to load booking data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingData();
  }, [id]);

  // Loading and Not Found states
  if (isLoading)
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );

  if (!booking)
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <main className="flex-grow pt-24 md:pt-32 text-center">
          <h1 className="text-2xl font-bold mb-4">Booking Not Found</h1>
          <p className="text-gray-600 mb-8">
            The booking you’re looking for doesn’t exist or has been removed.
          </p>
          <Button onClick={() => navigate("/services")}>Browse Services</Button>
        </main>
        <Footer />
      </div>
    );

  const totalPrice = Number(booking.totalPrice) || 0;
  const onlineFee = Number((totalPrice * 0.03).toFixed(2));

  // Handle Confirm
  const handleConfirm = async () => {
    if (!paymentType) {
      setError("Please select a payment method.");
      return;
    }
    setError("");

    const type = { type: paymentType };

    if (paymentType === "Direct" || paymentType === "Wallet") {
      try {
        await resortBookingStore.setPaymentTypeOfBooking(booking.id, type);
        toast.success("Booking payment type set successfully");
      } catch {
        toast.error("Error updating payment type.");
      }
    } else if (paymentType === "Online") {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("Type", "resort");
      formData.append("TransactionAmount", (totalPrice + onlineFee).toFixed(2));
      formData.append("IDS", booking.id.toString());
      formData.append("OrderName", booking.serviceName || "Resort Booking");

      try {
        const result = await paymentStore.initiatePayment(formData);
        if (result?.status === "success") {
          await resortBookingStore.setPaymentTypeOfBooking(booking.id, type);
          window.location.href = result.data as string;
        } else {
          toast.error("Payment initiation failed!");
        }
      } catch {
        toast.error("Error processing online payment.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Determine button text dynamically
  const confirmButtonText =
    paymentType === "Online"
      ? `Confirm Booking - AED ${(totalPrice + onlineFee).toFixed(2)}`
      : `Confirm Booking - AED ${totalPrice}`;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <CheckoutPageAnimator />
      <Header />

      <main className="flex-grow pt-24 md:pt-32">
        <section className="pb-16">
          <div className="custom-container mx-auto px-4 max-w-7xl grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* LEFT: Booking Details */}
            <div className="lg:col-span-2">
              <Card className="w-full shadow-md border-gray-200 bg-white rounded-xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-primary">
                    Booking Confirmation
                  </CardTitle>
                  <CardDescription>
                    Review your resort booking details below.
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Booking Confirmed</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-medium">{booking.customerName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Mobile No</p>
                        <p className="font-medium">{booking.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Check-in</p>
                        <p className="font-medium">
                          {formatDateTime(booking.startDateTime)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Check-out</p>
                        <p className="font-medium">
                          {formatDateTime(booking.endDateTime)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Guests */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Users className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Adults</p>
                        <p className="font-medium">{booking.adultsNumber}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Users className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Children</p>
                        <p className="font-medium">{booking.childrenNumber || 0}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* RIGHT: Checkout / Payment */}
            <div className="lg:col-span-1 sticky top-28">
              <Card className="border-gray-200 bg-white rounded-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">Order Summary</CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">
                        {booking.serviceName || "Resort Booking"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {booking.adultsNumber} Adults, {booking.childrenNumber || 0} Children
                      </p>
                    </div>
                    <span className="font-semibold">{totalPrice} AED</span>
                  </div>

                  <Separator />

                  {paymentType === "Online" && (
                    <>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Online Fee (3%)</span>
                        <span>{onlineFee} AED</span>
                      </div>
                      <div className="flex justify-between font-bold text-primary">
                        <span>Total with Fee:</span>
                        <span>{(totalPrice + onlineFee).toFixed(2)} AED</span>
                      </div>
                    </>
                  )}
                </CardContent>

                <CardFooter className="flex-col gap-4">
                  <div className="w-full">
                    <h3 className="font-semibold text-lg mb-3">Payment Method</h3>
                    {!booking.paymentType ? (
                      <div className="space-y-2">
                        {["Direct", "Online", ...(isPartner() ? ["Wallet"] : [])].map((type) => (
                          <label
                            key={type}
                            className="flex items-center justify-between border p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <span>
                              {type === "Direct" ? "Cash Payment" : type + " Payment"}
                            </span>
                            <input
                              type="radio"
                              value={type}
                              checked={paymentType === type}
                              onChange={(e) => setPaymentType(e.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">
                        Already selected: <strong>{booking.paymentType}</strong>
                      </p>
                    )}
                  </div>

                  {error && <p className="text-red-600 text-sm">{error}</p>}

                  <div className="w-full">
                    <Button
                      onClick={handleConfirm}
                      disabled={isLoading}
                      className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        confirmButtonText
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      By confirming, you agree to our Terms & Conditions.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap justify-center">
                    {paymentLogos.map((logo) => (
                      <img
                        key={logo.alt}
                        src={logo.src}
                        alt={logo.alt}
                        width={40}
                        height={26}
                        className="border rounded-md p-1 bg-gray-100"
                      />
                    ))}
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default observer(ResortBookingCheckout);
